import { CreateButton, EditButton, SaveButton } from '@refinedev/antd';
import { ToolList, embeddedTableProps } from '../../components/ToolList';

import {
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
} from 'antd';

import {
  IResourceComponentsProps,
  useGetIdentity,
  useGetLocale,
  useTranslate,
} from '@refinedev/core';

import { useCcEditableTable } from '../../helpers';
import { getYupSyncValidator } from '../../helpers/validation';

import {
  baseEmotionsEn,
  baseEmotionsRu,
  baseFeelingsEn,
  baseFeelingsRu,
  basicFeelingsEn,
  basicFeelingsRu,
  bodyPartsEn,
  bodyPartsRu,
  dbCols,
  getValidators,
  Thought,
  translationEn,
  UserRole,
} from '@cc/schema';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useContext, useEffect, useMemo } from 'react';
import { RuleObject } from 'antd/lib/form';
import { useFormSubmitDisabledAnyTouched } from '../../hooks';
import {
  ClientName,
  ShowNamesButton,
  clientFilterText,
  useClientNamesVisibility,
} from '../../components/clientNames';
import { useClientRoster } from '../../hooks/client-options';
import { colors } from '../../styles/colors';
import { numberToOnesAndZerosArrayOfArrays } from '@cc/utils';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { IUser } from '../../providers';
import { TherapistActivityReactContext } from '../../App';
import { useActor } from '@xstate/react';
import { TherapistActivityService } from '../../machines/therapistActivityMachine';
import { useActivityTracking } from '../../hooks/use-activity-tracking';
import { backToListEvent } from '../../hooks/activityEvents';
import { UnfinishedThoughtTests } from '../../components/UnfinishedThoughtTests';
import { useNavigate } from 'react-router-dom';

/**
 * E57 · EMBEDDED on the client page with the client FIXED (`fixedClientId` — registry decision `per-client-views`):
 * the same list, filtered to that client whatever the header bar's filter says, with no client column; Create opens the
 * create page with `?clientId=` (Q-e57g1-2 A rule 4); the page that hosts it records the RTM page view (rule 2), so
 * the list's own tracking is off. Standalone (no `fixedClientId`) it behaves exactly as before.
 */
export const ThoughtList: React.FC<
  IResourceComponentsProps & { fixedClientId?: string }
> = ({ fixedClientId }) => {
  const translate = useTranslate();
  const { t, i18n } = useTranslation();

  const therapistActivityService = useContext(TherapistActivityReactContext);

  const [therapistActivityState, therapistActivitySend] = useActor(
    therapistActivityService as TherapistActivityService,
  );
  // E57 — the client page fixes the client; otherwise the header bar's filter
  const clientFilterId = fixedClientId || therapistActivityState?.context?.filterClientId;
  const navigateTo = useNavigate();

  /**
   * WHOSE unfinished work to show. The page filters by the therapist's current client, but a link
   * carrying `?clientId=` should land on that client's list ready — the create page already reads
   * the URL this way, and a deep link that showed somebody else's filter would be worse than none.
   */
  const unfinishedSubject = (() => {
    const search = window.location.search;
    const fromUrl = new URLSearchParams(
      search.charAt(0) === '?' ? search.slice(1) : search,
    ).get('clientId');
    return (
      clientFilterId || fromUrl || undefined
    );
  })();

  const locale = useGetLocale();

  const currentLocale = locale();

  const {
    data: identity,
    remove,
    refetch: refetchIdentity,
  } = useGetIdentity<IUser>();

  const { portalThoughtUpdateValidator } = useMemo(
    () => getValidators(translate),
    [i18n.languages[0]],
  );

  const validationRules = getYupSyncValidator(
    translate,
    portalThoughtUpdateValidator,
  ) as RuleObject;

  // the WHOLE practice roster (P011) — the client column, the client filter and the edit gate below
  // all have to be able to name the owner of every row this table can render
  // Q-mt-3b A: "id · initials", full name on hover; Show names for 60 s
  const names = useClientNamesVisibility();
  const { clients, clientsLoading } = useClientRoster();

  const {
    table: { tableProps, setFilters },
    form: { formProps },
    isEditing,
    saveButtonProps,
    cancelButtonProps,
    editButtonProps,
  } = useCcEditableTable<Thought>(
    {
      // E57: the resource is named, never inferred from the route (embedded on the client page the route's is `clients`)
      resource: 'portalThoughts',
      queryOptions: {
        select: (data) => {
          // @ts-ignore
          return { data: data.data.data, total: data.total };
        },
      },
      sorters: {
        initial: [
          {
            field: 'updatedAt',
            order: 'desc',
          },
        ],
      },

      filters: {
        initial: clientFilterId
          ? [
              {
                field: 'userId',
                operator: 'eq',
                value: clientFilterId,
              },
              // Note: it's a workaround for Refine not sending nulls
              {
                field: 'activatingEventId',
                operator: 'ne',
                value: 'null',
              },
            ]
          : [
              {
                field: 'activatingEventId',
                operator: 'ne',
                value: 'null',
              },
            ],
      },
      meta: {
        fields: [
          {
            data: [
              '_id',
              'userId',
              'automaticThought',
              'thoughtBelief',
              'thoughtsFor',
              'thoughtsForAlternativeThoughts',
              'thoughtsAgainst',
              'cognitiveDistortions',
              { realisticThoughts: ['thought', 'thoughtBelief'] },
              {
                result: [
                  'initialThoughtBelief',
                  'initialNegativeEmotionStrenght',
                ],
              },
              'updatedAt',
              {
                activatingEvent: [
                  'description',
                  { emotions: ['id', 'strength'] },
                  'feelings',
                  'bodyPartsFeelings',
                  'behaviors',
                ],
              },
            ],
          },
          'count',
        ],
      },
    },
    {
      resource: 'portalThoughts',
      onMutationSuccess: () => {
        cancelButtonProps.onClick?.();
      },
      meta: {
        fields: [
          '_id',
          'userId',
          'automaticThought',
          'thoughtBelief',
          'thoughtsFor',
          'thoughtsForAlternativeThoughts',
          'thoughtsAgainst',
          { realisticThoughts: ['thought', 'thoughtBelief'] },
          {
            result: ['initialThoughtBelief', 'initialNegativeEmotionStrenght'],
          },
          'updatedAt',
          {
            activatingEvent: [
              'description',
              { emotions: ['id', 'strength'] },
              'feelings',
              'bodyPartsFeelings',
              'behaviors',
            ],
          },
        ],
      },
    },
  );

  useEffect(() => {
    setFilters(
      clientFilterId
        ? [
            {
              field: 'userId',
              operator: 'eq',
              value: clientFilterId,
            },
            {
              field: 'activatingEventId',
              operator: 'ne',
              value: 'null',
            },
          ]
        : [
            {
              field: 'activatingEventId',
              operator: 'ne',
              value: 'null',
            },
          ],
      'replace',
    );
  }, [clientFilterId]);

  const {
    handleFormChange,
    isDisabled: isSubmitDisabled,
    fieldsCount,
  } = useFormSubmitDisabledAnyTouched(formProps?.form!);

  const baseEmotions = useMemo(() => {
    const baseEmotions = i18n.languages[0]?.startsWith('ru')
      ? baseEmotionsRu
      : baseEmotionsEn;

    return baseEmotions.map((e) => ({
      ...e,
      children: e.children.map((ch) => ({ ...ch, strength: 50 })),
    }));
  }, [i18n.languages[0]]);

  const flattenedEmotions = useMemo(() => {
    return baseEmotions.reduce(
      (acc, val) => [...acc, ...val.children],
      [] as (typeof baseEmotionsEn)[0]['children'],
    );
  }, [baseEmotions]);

  const baseFeelings = i18n.languages[0]?.startsWith('ru')
    ? baseFeelingsRu
    : baseFeelingsEn;

  const flattenedFeelings = useMemo(() => {
    return baseFeelings.reduce(
      (acc, val) => [...acc, ...val.children],
      [] as (typeof baseFeelings)[0]['children'],
    );
  }, [baseFeelings]);

  const headerFeelings = i18n.languages[0]?.startsWith('ru')
    ? basicFeelingsRu
    : basicFeelingsEn;

  const bodyParts = i18n.languages[0]?.startsWith('ru')
    ? bodyPartsRu
    : bodyPartsEn;

  const feelingsColumns = useMemo(() => {
    return [
      {
        title: '',
        dataIndex: 'feelingName',
      },
      ...headerFeelings.map((feeling) => {
        return {
          title: feeling,
          dataIndex: feeling,
          render: (value: any, record: any) => {
            return <Checkbox checked={!!value} disabled />;
          },
        };
      }),
    ];
  }, [headerFeelings]);

  useActivityTracking(dbCols.thoughts, fixedClientId, !fixedClientId);

  const expandedRowRender = (record: Thought) => {
    const bodyPartsAsArray = numberToOnesAndZerosArrayOfArrays(
      headerFeelings.length,
      bodyParts.length,
      // @ts-ignore
      record.activatingEvent.bodyPartsFeelings,
    );

    const feelingsData = bodyPartsAsArray.map((row, i) => {
      return Object.assign(
        {},
        ...[
          { key: i },
          {
            feelingName: bodyParts[i],
          },
          ...row.map((col, j) => {
            return {
              [headerFeelings[j]]: col,
            };
          }),
        ],
      );
    });

    return (
      <div>
        <Row>
          <Col span={12}>
            <span style={{ marginRight: 10 }}>
              {translate('createThought.emotionsTitle')}:{' '}
            </span>
            {
              // @ts-ignore
              record.activatingEvent.emotions
                // @ts-ignore
                .sort((a, b) => b.strength - a.strength)
                // @ts-ignore
                .map((e) => {
                  let color =
                    e.strength < 20
                      ? colors['--triad-green-0']
                      : e.strength < 40
                      ? colors['--triad-green-1']
                      : e.strength < 60
                      ? colors['--primary-1']
                      : e.strength < 80
                      ? colors['--primary-2']
                      : colors['--primary-0'];

                  return (
                    <Tag color={color} key={e.id}>
                      {`${
                        flattenedEmotions.find((fe) => fe.id === e.id)?.name
                      } - ${e.strength}`}
                    </Tag>
                  );
                })
            }
            <div style={{ marginTop: 20, marginBottom: 20 }}>
              <span style={{ marginRight: 10 }}>
                {translate('createThought.feelingsTitle')}:{' '}
              </span>
              {
                // @ts-ignore
                record.activatingEvent.feelings.map((f) => {
                  const maybeFeelingName = flattenedFeelings.find(
                    (bf) => bf.id === f,
                  )?.name;
                  if (!maybeFeelingName) return null;

                  return <Tag key={f}>{maybeFeelingName}</Tag>;
                })
              }
            </div>
            <div style={{ marginTop: 20, marginBottom: 20 }}>
              <span style={{ marginRight: 10 }}>
                {translate('shared.abcModel.behaviors')}:{' '}
              </span>
              {
                // @ts-ignore
                record.activatingEvent.behaviors
              }
            </div>
          </Col>
          <Col span={12}>
            <div>
              {translate('createThought.feelingsInBody')}:{' '}
              <Table
                columns={feelingsColumns}
                dataSource={feelingsData}
                pagination={false}
              />
            </div>
          </Col>
        </Row>
      </div>
    );
  };

  return (
    <ToolList
      fixedClientId={fixedClientId}
      tableProps={tableProps}
      help={translate('unfinishedThoughtTests.intro')}
      breadcrumb={fixedClientId ? false : undefined}
      title={translate('pages.thoughtList.title')}
      headerProps={{
        extra: (
          <Space>
            {fixedClientId ? null : (
              <ShowNamesButton
                visible={names.visible}
                onToggle={names.toggle}
                testId="cc-thoughts-toggle-names"
              />
            )}
            <CreateButton
            data-testid="create-thought-test-button"
            onClick={
              fixedClientId
                ? () => navigateTo(`/tools/portalThoughts/create?clientId=${encodeURIComponent(fixedClientId)}`)
                : undefined
            }
          />
          </Space>
        ),
      }}
    >
      {/*
        What this client STARTED and did not finish — above the finished ones, because it is the
        part a session works on and the part no legacy table can show (the app writes a `thoughts`
        row only when the result step is left). Scoped to the client the list is filtered to; with
        no client chosen there is nobody to ask about, and the section does not render.
      */}
      <UnfinishedThoughtTests subject={unfinishedSubject} />

      <Form {...formProps} onFieldsChange={handleFormChange}>
        <Table
          {...embeddedTableProps(fixedClientId)}
          {...tableProps}
          rowKey="_id"
          expandable={{
            expandedRowRender,
          }}
          onChange={(pagination, filters, sorter, extra) => {
            // Note: The data we get from Ant.design filters does not match the
            // react refine one. Currently it works fine if I simply silence it the way below.
            // TOOD: Find a way to convert the data to match the react refine one and fix it better
            // @ts-ignore
            tableProps?.onChange?.(pagination, null, sorter, extra);
          }}
          pagination={{
            ...tableProps.pagination,
            showSizeChanger: true,
          }}
          data-testid="thought-tests-list"
        >
          {fixedClientId ? null : (
          <Table.Column
            key="_id"
            dataIndex="userId"
            sorter={{ multiple: 2 }}
            title={translate('pages.thoughtList.userId')}
            filterSearch
            // @ts-ignore
            onFilter={(value: string, record: Thought) => {
              return record.userId === value;
            }}
            filters={clients.map((c) => {
              return {
                text: clientFilterText(c, names.visible),
                value: c._id,
              };
            })}
            render={(value, record) => {
              const client = clients.find((c) => c._id === value);

              return (
                <ClientName
                  id={value}
                  firstName={client?.firstName}
                  lastName={client?.lastName}
                  visible={names.visible}
                />
              );
            }}
          />
          )}
          <Table.Column<Thought>
            key="activatingEvent.description"
            dataIndex={['activatingEvent', 'description']}
            title={translate('pages.thoughtList.activatingEvent')}
          />
          <Table.Column<Thought>
            key="automaticThought"
            dataIndex="automaticThought"
            title={translate('pages.thoughtList.automaticThought')}
            render={(value, record) => {
              if (isEditing(record._id)) {
                return (
                  <Form.Item
                    name={['automaticThought']}
                    style={{ margin: 0, minWidth: 120 }}
                    rules={[validationRules]}
                  >
                    <Input.TextArea autoSize={{ minRows: 4 }} />
                  </Form.Item>
                );
              }
              return value;
            }}
          />
          <Table.Column<Thought>
            key="thoughtsFor"
            dataIndex="thoughtsFor"
            title={translate('pages.thoughtList.thoughtsFor')}
            render={(value, record) => {
              if (isEditing(record._id)) {
                return (
                  <Form.List name={['thoughtsFor']}>
                    {(fields, { add, remove }, errors) => {
                      return (
                        <>
                          {fields.map((field, index) => {
                            return (
                              <Form.Item
                                {...field}
                                key={field.key}
                                name={[field.key]}
                                style={{ margin: 0, minWidth: 120 }}
                                rules={[
                                  {
                                    message: translate(
                                      'createThought.thoughtEvidenceRequired',
                                    ),
                                  },
                                ]}
                              >
                                <Input.TextArea autoSize={{ minRows: 2 }} />
                              </Form.Item>
                            );
                          })}
                        </>
                      );
                    }}
                  </Form.List>
                );
              }
              // @ts-ignore
              return (value || []).map((v, idx) => (
                <div key={idx}>
                  {value?.length > 1 ? `${idx + 1}. ${v}` : v}
                </div>
              ));
            }}
          />
          <Table.Column<Thought>
            key="thoughtsForAlternativeThoughts"
            dataIndex="thoughtsForAlternativeThoughts"
            title={translate(
              'pages.thoughtList.thoughtsForAlternativeThoughts',
            )}
            render={(value, record) => {
              if (isEditing(record._id)) {
                return (
                  <Form.List name={['thoughtsForAlternativeThoughts']}>
                    {(fields, { add, remove }, errors) => {
                      return (
                        <>
                          {fields.map((field, index) => {
                            return (
                              <Form.Item
                                {...field}
                                key={field.key}
                                name={[field.key]}
                                style={{ margin: 0, minWidth: 120 }}
                              >
                                <Input.TextArea autoSize={{ minRows: 2 }} />
                              </Form.Item>
                            );
                          })}
                        </>
                      );
                    }}
                  </Form.List>
                );
              }
              // @ts-ignore
              return (value || []).map((v, idx) => (
                <div key={idx}>
                  {value?.length > 1 ? `${idx + 1}. ${v}` : v}
                </div>
              ));
            }}
          />
          <Table.Column<Thought>
            key="thoughtsAgainst"
            dataIndex="thoughtsAgainst"
            title={translate('pages.thoughtList.thoughtsAgainst')}
            render={(value, record) => {
              if (isEditing(record._id)) {
                return (
                  <Form.List name={['thoughtsAgainst']}>
                    {(fields, { add, remove }, errors) => {
                      return (
                        <>
                          {fields.map((field, index) => {
                            return (
                              <Form.Item
                                {...field}
                                key={field.key}
                                name={[field.key]}
                                style={{ margin: 0, minWidth: 120 }}
                                rules={[
                                  {
                                    message: translate(
                                      'createThought.evidenceAgainstRequired',
                                    ),
                                  },
                                ]}
                              >
                                <Input.TextArea autoSize={{ minRows: 2 }} />
                              </Form.Item>
                            );
                          })}
                        </>
                      );
                    }}
                  </Form.List>
                );
              }
              // the items are a list, as in the evidence-for column: one line each, numbered when there are several
              return ((value as string[] | undefined) || []).map((v, idx) => (
                <div key={idx}>
                  {value?.length > 1 ? `${idx + 1}. ${v}` : v}
                </div>
              ));
            }}
          />
          <Table.Column<Thought>
            key="cognitiveDistortions"
            dataIndex={['cognitiveDistortions']}
            title={translate('createThought.cognitiveDistortions')}
            render={(distortionsKeys, record, indx) => {
              if (isEditing(record._id)) {
                return (
                  <Form.Item
                    name={'cognitiveDistortions'}
                    style={{ margin: 0, minWidth: 140 }}
                    rules={[validationRules]}
                  >
                    <Select
                      placeholder={translate(
                        'createThought.cognitiveDistortionsPlaceholder',
                      )}
                      defaultValue={distortionsKeys || []}
                      optionFilterProp="children"
                      loading={clientsLoading}
                      mode="multiple"
                      allowClear
                      style={{ width: 400 }}
                    >
                      {Object.keys(translationEn.cognitiveDistorions).map(
                        (key) => (
                          <Select.Option key={key} value={key}>
                            {translate(`cognitiveDistorions.${key}`)}
                          </Select.Option>
                        ),
                      )}
                    </Select>
                  </Form.Item>
                );
              }
              return (
                <div>
                  {(distortionsKeys || []).map((key: string, idx: number) => (
                    <div key={idx}>{`${idx + 1}. ${translate(
                      `cognitiveDistorions.${key}`,
                    )}`}</div>
                  ))}
                </div>
              );
            }}
          />
          <Table.Column<Thought>
            key="realisticThoughts"
            dataIndex={['realisticThoughts']}
            title={translate('pages.thoughtList.realisticThoughts')}
            render={(thoughts, record, indx) => {
              if (isEditing(record._id)) {
                return (
                  <Form.List name={['realisticThoughts']}>
                    {(fields, { add, remove }, errors) => {
                      return (
                        <>
                          {fields.map((field, index) => {
                            return (
                              <Form.Item
                                {...field}
                                key={field.key}
                                name={[field.key, 'thought']}
                                style={{ margin: 0, minWidth: 140 }}
                                rules={[
                                  {
                                    required: true,
                                    message: translate(
                                      'createThought.realisticThoughtRequired',
                                    ),
                                  },
                                ]}
                              >
                                <Input.TextArea autoSize={{ minRows: 3 }} />
                              </Form.Item>
                            );
                          })}
                        </>
                      );
                    }}
                  </Form.List>
                );
              }
              return (
                <div>
                  {
                    // @ts-ignore
                    thoughts.map((t, i) => {
                      const prefix = i === 0 ? '' : `${i + 1}. `;
                      return <div key={i}>{`${prefix}${t.thought}`}</div>;
                    })
                  }
                </div>
              );
            }}
          />
          <Table.Column
            key="updatedAt"
            dataIndex="updatedAt"
            title={translate('pages.thoughtList.updatedAt')}
            sorter={{ multiple: 1 }}
            render={(text, record, indx) => {
              const date = new Date(text);
              return format(
                date,
                currentLocale?.startsWith('ru')
                  ? 'yyyy-MM-dd HH:mm:ss'
                  : 'yyyy-MM-dd hh:mm:ss aaa',
              );
            }}
          />
          <Table.Column<Thought>
            title={translate('resourcesCommon.actions')}
            dataIndex="actions"
            render={(_, record) => {
              const client = clients.find((c) => c._id === record.userId);

              const hasPremiumAccess = client?.accessLevel;

              const canEdit =
                client?.access?.allowEdit ||
                (client?.access?.allowAdminEdit &&
                  identity?.db?.role === UserRole.THERAPIST_ADMIN);

              if (isEditing(record._id)) {
                return (
                  <Space>
                    <SaveButton
                      {...saveButtonProps}
                      onClick={() => {
                        therapistActivitySend({
                          type: 'UPDATED',
                          resource: record._id,
                        });
                        saveButtonProps.onClick?.();

                        // back to the list for ITS client (on the client page the header's filter is empty — review F7)
                        therapistActivitySend(backToListEvent(dbCols.thoughts, clientFilterId));
                      }}
                      disabled={isSubmitDisabled}
                      size="small"
                    />
                    <Button
                      {...cancelButtonProps}
                      size="small"
                      onClick={() => {
                        therapistActivitySend({
                          type: 'TIMED_OUT',
                          resource: record._id,
                        });
                        cancelButtonProps.onClick?.();
                        // back to the list for ITS client (on the client page the header's filter is empty — review F7)
                        therapistActivitySend(backToListEvent(dbCols.thoughts, clientFilterId));
                      }}
                    >
                      {translate('buttons.cancel')}
                    </Button>
                  </Space>
                );
              }
              return (
                <Space>
                  {
                    <>
                      <EditButton
                        {...editButtonProps(record._id)}
                        onClick={() => {
                          therapistActivitySend({
                            type: 'TIMED_OUT',
                            resource: dbCols.thoughts,
                          });
                          therapistActivitySend({
                            type: 'LOADED',
                            resource: record._id,
                            clientId: record.userId,
                          });
                          editButtonProps(record._id).onClick?.();
                        }}
                        hideText
                        size="small"
                        disabled={!hasPremiumAccess || !canEdit}
                      />
                      {(!hasPremiumAccess || !canEdit) && (
                        <Tooltip
                          title={translate(
                            !hasPremiumAccess
                              ? 'pages.thoughtList.thoughtEditLimitTip'
                              : 'pages.thoughtList.thoughtEditNoPermissionTip',
                          )}
                        >
                          <QuestionCircleOutlined
                            style={{ marginLeft: '10px' }}
                          />
                        </Tooltip>
                      )}
                    </>
                  }
                </Space>
              );
            }}
          />
        </Table>
      </Form>
    </ToolList>
  );
};
